import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import { useState, useEffect, useContext, useMemo } from "react";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import ConfirmModal from "../components/ConfirmModal";
import {
  Plus,
  Search,
  Trash2,
  Users,
  CheckSquare,
  Square,
  ChevronLeft,
  ChevronRight,
  LayoutList,
  X,
} from "lucide-react";

const SupplierGroupMaster = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // Form visibility
  const [showForm, setShowForm] = useState(false);

const [groupName, setGroupName] = useState("");
  const [editingGroup, setEditingGroup] = useState(null);
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [search, setSearch] = useState("");
const [loading, setLoading] = useState(false);
  
const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const res = await axios.get("https://nissi-india.com/api/supplier_groups", {
        params: { page, pageSize, search },
        headers: commonHeaders,
      });

      setGroups(res.data?.groups || []);
      setTotalCount(res.data?.totalItems || 0);
      setTotalPages(res.data?.totalPages || 0);
    } catch (err) {
      console.error("fetchGroups error:", err.response?.data || err.message);
      toast.error("Failed to fetch supplier groups");
    } finally {
      setLoading(false);
    }
  };

const handleAddGroup = async () => {
    if (!groupName.trim()) {
      toast.warning("Please enter a group name");
      return;
    }

    try {
      const res = editingGroup
        ? await axios.put(
            `https://nissi-india.com/api/supplierGroups_update/${editingGroup.fld_groupid}`,
            {
              groupName: groupName.trim(),
              loginUserId: userId,
            },
            { headers: commonHeaders }
          )
        : await axios.post(
            "https://nissi-india.com/api/supplierGroups_insert",
            {
              groupName: groupName.trim(),
              loginUserId: userId,
            },
            { headers: commonHeaders }
          );

      if (res.data.success) {
        toast.success(editingGroup ? "Group updated successfully" : "Group added successfully");
        setGroupName("");
        setEditingGroup(null);
        setShowForm(false);
        if (!editingGroup) setPage(1);
        fetchGroups();
      } else {
        toast.error(res.data?.error || "Failed to save group");
      }
    } catch (err) {
      console.error("handleAddGroup error:", err.response?.data || err.message);
      toast.error(err.response?.data?.error || "Error saving group");
    }
  };



  const openEditForm = (group) => {
    setEditingGroup(group);
    setGroupName(group.fld_groupname);
    setShowForm(true);
  };
  const handleCloseForm = () => {
    setGroupName("");
    setShowForm(false);
  };

const handleDeleteSelected = () => {
    if (selectedGroups.length === 0) return;
    setShowDeleteConfirm(true);
  };

const confirmDeleteSelected = async () => {
  setShowDeleteConfirm(false);

  try {
    const res = await axios.post(
      "https://nissi-india.com/api/supplierGroups_delete/delete",
      { groupIds: selectedGroups },
      { headers: commonHeaders }
    );

      if (res.data.success) {
        toast.success("Groups deleted successfully");
        setSelectedGroups([]);
        fetchGroups();
      } else {
        toast.error("Failed to delete: " + (res.data.message || "Unknown error"));
      }
    } catch (err) {
      console.error(
        "handleDeleteSelected error:",
        err.response?.data || err.message
      );
      toast.error(
        "Error deleting groups: " +
          (err.response?.data?.message || err.message)
      );
    }
  };

const handleSelectAll = () => {
  if (selectedGroups.length === groups.length) {
    setSelectedGroups([]);
  } else {
    // ✅ FIX: track by fld_groupid, not fld_groupname — names are
    // free-text and not a safe unique key for delete operations.
    setSelectedGroups(groups.map((group) => group.fld_groupid));
  }
};

const handleCheckboxChange = (id) => {
  setSelectedGroups((prev) =>
    prev.includes(id)
      ? prev.filter((gid) => gid !== id)
      : [...prev, id]
  );
};

  useEffect(() => {
    fetchGroups();
  }, [page, search, commonHeaders]);

  return (
    <LayoutWrapper>
      <HeaderBar
        title={
          <span className="font-dmserif text-3xl">Supplier Group Master</span>
        }
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div className="space-y-6 mt-6">
        {/* Header Card */}
        <div
          className={`p-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold mb-1">Supplier Group Management</h1>
              <p className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Create and manage supplier groups for better organization
              </p>
            </div>

            <div className="flex items-center gap-3">
              <div
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
                  isDark ? "bg-gray-800/60 text-gray-300" : "bg-gray-100 text-gray-600"
                }`}
              >
                <Users size={16} />
                {totalCount} Groups
              </div>

              {selectedGroups.length > 0 && (
                <div
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
                    isDark ? "bg-red-900/40 text-red-300" : "bg-red-100 text-red-700"
                  }`}
                >
                  <Trash2 size={16} />
                  {selectedGroups.length} Selected
                </div>
              )}

              {/* Add New Button */}
              <button
                onClick={() => setShowForm(true)}
                className={`inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-xl shadow transition ${buttonClass} ${hoverClass}`}
              >
                <Plus size={18} />
                Add New Group
              </button>
            </div>
          </div>
        </div>

        {/* ==================== COLLAPSIBLE FORM ==================== */}
        {showForm && (
          <div
            className={`p-6 rounded-2xl shadow-xl border ${
              isDark
                ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
                : `bg-white ${currentSkin.glass} text-black border-gray-200`
            }`}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div
                  className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
                >
                  <Plus size={20} className="text-white" />
                </div>
<div>
                  <h2 className="text-lg font-bold">
                    {editingGroup ? "Edit Group" : "Add New Group"}
                  </h2>
                  <p className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                    {editingGroup
                      ? "Update the supplier group name"
                      : "Enter a name to create a new supplier group"}
                  </p>
                </div>
              </div>

              {/* Close Button */}
              <button
                onClick={handleCloseForm}
                className={`p-2 rounded-lg transition ${
                  isDark
                    ? "hover:bg-gray-700 text-gray-300"
                    : "hover:bg-gray-100 text-gray-600"
                }`}
                title="Close"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label
                  className={`block text-xs font-semibold tracking-wide mb-1.5 ${
                    isDark ? "text-slate-300" : "text-slate-700"
                  }`}
                >
                  Group Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter supplier group name..."
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddGroup()}
                  className={`w-full px-4 py-2.5 rounded-lg border text-sm transition focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                    isDark
                      ? "bg-gray-800/50 border-gray-600 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                  }`}
                  autoFocus
                />
              </div>

              <div className="flex items-end gap-3">
                <button
                  onClick={handleCloseForm}
                  className={`px-5 py-2.5 text-sm font-medium rounded-lg border transition ${
                    isDark
                      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  Cancel
                </button>

<button
                  onClick={handleAddGroup}
                  disabled={!groupName.trim()}
                  className={`inline-flex items-center gap-2 px-6 py-2.5 text-white text-sm font-semibold rounded-lg shadow transition ${
                    !groupName.trim()
                      ? "bg-gray-400 cursor-not-allowed"
                      : `${buttonClass} ${hoverClass}`
                  }`}
                >
                  <Plus size={16} />
                  {editingGroup ? "Update Group" : "Save Group"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Search + Actions */}
        <div
          className={`p-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black border-gray-200`
          }`}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex-1 max-w-md w-full">
              <label
                className={`block text-xs font-semibold tracking-wide mb-1.5 ${
                  isDark ? "text-slate-300" : "text-slate-700"
                }`}
              >
                Search Groups
              </label>
              <div className="relative">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  placeholder="Search by group name..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className={`w-full pl-10 pr-4 py-2.5 rounded-lg border text-sm transition focus:outline-none focus:ring-2 focus:ring-blue-400 ${
                    isDark
                      ? "bg-gray-800/50 border-gray-600 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                  }`}
                />
              </div>
            </div>

            <div className="flex items-center gap-3 mt-6 sm:mt-0">
              <button
                onClick={handleSelectAll}
                disabled={groups.length === 0}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-lg transition ${
                  isDark
                    ? "bg-blue-600 hover:bg-blue-700 text-white"
                    : "bg-blue-500 hover:bg-blue-600 text-white"
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {selectedGroups.length === groups.length && groups.length > 0 ? (
                  <>
                    <Square size={16} />
                    Deselect All
                  </>
                ) : (
                  <>
                    <CheckSquare size={16} />
                    Select All
                  </>
                )}
              </button>

              <button
                onClick={handleDeleteSelected}
                disabled={selectedGroups.length === 0}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg shadow transition ${
                  selectedGroups.length === 0
                    ? "bg-gray-400 cursor-not-allowed text-white"
                    : "bg-red-600 hover:bg-red-700 text-white"
                }`}
              >
                <Trash2 size={16} />
                Delete ({selectedGroups.length})
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div
          className={`rounded-2xl shadow-xl border overflow-hidden ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
              : `bg-white ${currentSkin.glass} border-gray-200`
          }`}
        >
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}></div>
              <div>
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <LayoutList size={20} />
                  Supplier Groups
                </h3>
                <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                  Manage your supplier groups and categories
                </p>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead
                className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
              >
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                    S.No
                  </th>
<th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">
                    Group Name
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">
                    Edit
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider">
                    <div className="flex items-center justify-center gap-2">
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded"
                        checked={
                          selectedGroups.length === groups.length &&
                          groups.length > 0
                        }
                        onChange={handleSelectAll}
                      />
                      Select
                    </div>
                  </th>
                </tr>
              </thead>

              <tbody
                className={`divide-y ${isDark ? "divide-gray-600" : "divide-gray-200"}`}
              >
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(4)].map((_, j) => (
                        <td key={j} className="px-6 py-4">
                          <Skeleton
                            height={24}
                            baseColor={isDark ? "#374151" : "#f3f4f6"}
                          />
                        </td>
                      ))}
                    </tr>
                  ))
) : groups.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center py-16">
                      <div className="flex flex-col items-center gap-3">
                        <Users size={48} className="text-gray-400" />
                        <div className="text-lg font-semibold text-gray-500">
                          No supplier groups found
                        </div>
                        <div className="text-sm text-gray-400">
                          {search
                            ? "Try adjusting your search terms"
                            : "Click “Add New Group” to create one"}
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  groups.map((group, index) => (
                    <tr
                      key={group.fld_groupid}
                      className={`transition ${
  isDark
    ? "bg-gray-800/20 hover:bg-gray-700/30"
    : "bg-white hover:bg-gray-50"
} ${
  selectedGroups.includes(group.fld_groupid)
    ? isDark
      ? "bg-blue-900/20"
      : "bg-blue-50"
    : ""
}`}
                    >
                      <td className="px-6 py-4">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                            isDark
                              ? "bg-gray-700 text-gray-200"
                              : `bg-${baseColor}-100 text-${baseColor}-800`
                          }`}
                        >
                          {(page - 1) * pageSize + index + 1}
                        </div>
                      </td>

                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                              isDark ? "bg-gray-700/50" : "bg-gray-100"
                            }`}
                          >
                            <Users size={18} className="text-gray-500" />
                          </div>
                          <div>
                            <p className="font-medium">{group.fld_groupname}</p>
                            <p
                              className={`text-xs ${
                                isDark ? "text-gray-400" : "text-gray-500"
                              }`}
                            >
                              ID: {group.fld_groupid}
                            </p>
                          </div>
                        </div>
                      </td>

<td className="px-6 py-4 text-center">
                        <button
                          onClick={() => openEditForm(group)}
                          className={`p-1.5 rounded-full transition ${
                            isDark
                              ? "bg-yellow-900/30 text-yellow-400 hover:bg-yellow-900/50"
                              : "bg-yellow-100 text-yellow-600 hover:bg-yellow-200"
                          }`}
                          title="Edit"
                        >
                          ✏️
                        </button>
                      </td>

                      <td className="px-6 py-4 text-center">
                        <input
  type="checkbox"
  className="w-4 h-4 rounded"
  checked={selectedGroups.includes(group.fld_groupid)}
  onChange={() =>
    handleCheckboxChange(group.fld_groupid)
  }
/>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 gap-4">
              <div
                className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}
              >
                Showing{" "}
                <span className="font-semibold">
                  {(page - 1) * pageSize + 1}
                </span>{" "}
                to{" "}
                <span className="font-semibold">
                  {Math.min(page * pageSize, totalCount)}
                </span>{" "}
                of <span className="font-semibold">{totalCount}</span> groups
              </div>

              <div className="inline-flex rounded-lg shadow-sm overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                <button
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={page === 1}
                  className={`inline-flex items-center gap-1 px-4 py-2 text-sm font-medium transition ${
                    page === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                  }`}
                >
                  <ChevronLeft size={16} />
                  Prev
                </button>

                {[...Array(Math.min(totalPages, 5))].map((_, idx) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = idx + 1;
                  } else if (page <= 3) {
                    pageNum = idx + 1;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + idx;
                  } else {
                    pageNum = page - 2 + idx;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`px-4 py-2 text-sm font-medium transition ${
                        page === pageNum
                          ? `bg-${baseColor}-${baseShade} text-white`
                          : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() =>
                    setPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={page === totalPages}
                  className={`inline-flex items-center gap-1 px-4 py-2 text-sm font-medium transition ${
                    page === totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200"
                  }`}
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Floating Delete Bar */}
      {selectedGroups.length > 0 && (
        <div className="fixed bottom-6 right-6 z-50">
          <div
            className={`p-4 rounded-2xl shadow-2xl border backdrop-blur-md flex items-center gap-4 ${
              isDark
                ? "bg-gray-900/90 border-gray-700/50 text-white"
                : "bg-white/90 border-gray-200/60 text-gray-900"
            }`}
          >
            <div className="text-sm">
              <span className="font-semibold">{selectedGroups.length}</span> groups
              selected
            </div>
            <button
              onClick={handleDeleteSelected}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl font-medium bg-red-600 hover:bg-red-700 text-white shadow transition"
            >
              <Trash2 size={16} />
              Delete
            </button>
          </div>
        </div>
      )}

    <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <ConfirmModal
        open={showDeleteConfirm}
        title="Delete Supplier Groups"
        message={`Are you sure you want to delete ${selectedGroups.length} group(s)? This action cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={confirmDeleteSelected}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </LayoutWrapper>
  );
};

export default SupplierGroupMaster;